/**
 * MyClips V3 — Motion Grammar
 *
 * 6 named springs, 4 easing curves, one duration scale.
 * Every animation in V3 uses one of these — no custom one-offs.
 *
 * Reference: framer-motion spring API
 * https://motion.dev/docs/react-transitions
 */

export const duration = {
  instant: 80,
  quick: 160,
  base: 240,
  smooth: 320,
  slow: 480,
  epic: 600,
};

export const easing = {
  standard: [0.4, 0.0, 0.2, 1],
  decisive: [0.2, 0.0, 0.0, 1],
  gentle: [0.25, 0.1, 0.25, 1],
  iosSpring: [0.34, 1.56, 0.64, 1],
};

export const easingCss = {
  standard: "cubic-bezier(0.4, 0.0, 0.2, 1)",
  decisive: "cubic-bezier(0.2, 0.0, 0.0, 1)",
  gentle: "cubic-bezier(0.25, 0.1, 0.25, 1)",
  iosSpring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
};

export const springs = {
  soft: { type: "spring", stiffness: 220, damping: 26, mass: 0.9 },
  snap: { type: "spring", stiffness: 380, damping: 30, mass: 0.7 },
  crisp: { type: "spring", stiffness: 520, damping: 34, mass: 0.6 },
  sheet: { type: "spring", stiffness: 340, damping: 32, mass: 0.8 },
  hero: { type: "spring", stiffness: 180, damping: 24, mass: 1.0 },
  burst: { type: "spring", stiffness: 600, damping: 18, mass: 0.5 },
};

/**
 * Choreography recipes — event → animation preset.
 * Use these for consistency across the app.
 */
export const choreo = {
  chipTap: {
    scale: [1, 0.96, 1],
    transition: springs.snap,
  },
  likeBurst: {
    scale: [0.6, 1.15, 1],
    opacity: [0.9, 1, 1],
    transition: springs.burst,
  },
  sheetOpen: {
    y: ["100%", "0%"],
    transition: springs.sheet,
  },
  sheetClose: {
    y: ["0%", "100%"],
    transition: {
      duration: duration.base / 1000,
      ease: easing.decisive,
    },
  },
  errorShake: {
    x: [0, -4, 4, -3, 3, 0],
    transition: {
      duration: duration.smooth / 1000,
      ease: easing.standard,
    },
  },
  pullRefreshCommit: {
    scale: [0.9, 1.08, 1],
    transition: {
      duration: duration.slow / 1000,
      ease: easing.iosSpring,
    },
  },
  routePush: {
    opacity: [0, 1],
    y: [4, 0],
    transition: {
      duration: duration.base / 1000,
      ease: easing.gentle,
    },
  },
};

/**
 * Reduced-motion fallback: all springs collapse to a short opacity fade.
 * Wrap any animate={...} with this helper when motion-sensitivity matters.
 */
// Jenerik: preset AYNEN geri dönüyor, o yüzden tipi de aynen korunmalı.
// `unknown` yazılsaydı çağıranlar preset'in tipini kaybederdi — borç
// çağıranlara taşınırdı.
export function respectReducedMotion<T>(
  preset: T,
): T | { opacity: number[]; transition: { duration: number } } {
  if (typeof window === "undefined") return preset;
  const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  if (!mq || !mq.matches) return preset;
  return {
    opacity: [0, 1],
    transition: { duration: 0.12 },
  };
}

export default { duration, easing, easingCss, springs, choreo, respectReducedMotion };
